import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Box, Button, Paper, Stack, Typography } from '@mui/material'

import PageContainer from '@/components/ui/containers/PageContainer'
import DeleteConfirmationDialog from '@/components/ui/dialogs/DeleteConfirmationDialog'

import { useDeleteLicenseMutation, useGetLicenseQuery, useUpdateLicenseMutation } from '@/core/services/license'
import { useGetPoliciesQuery } from '@/core/services/policy'
import { Role } from '@/core/types/shared'

import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { useAppSelector } from '@/redux/rootReducer'

import { getApiErrorMessage } from '@/utils/errors'

import LicenseForm from './components/LicenseForm'

export default function LicensePage() {
  const { licenseId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const authUser = useAppSelector(selectAuthUser)

  const { data: license, isLoading } = useGetLicenseQuery(licenseId ?? '', { skip: !licenseId })
  const { data: policies } = useGetPoliciesQuery()
  const [updateLicense, { isLoading: isUpdatingLicense }] = useUpdateLicenseMutation()
  const [deleteLicense, { isLoading: isDeletingLicense }] = useDeleteLicenseMutation()

  const [editable, setEditable] = useState(false)
  const [openDeleteConfirmationDialog, setOpenDeleteConfirmationDialog] = useState(false)

  const initialValues = useMemo(
    () =>
      license
        ? {
          components: license.components,
          name: license.name,
          description: license.description,
          isDefault: license.isDefault ?? false,
          policies: license.policies.map((policy) => policy._id),
          services: license.services,
        }
        : undefined,
    [license]
  )

  const policiesData = useMemo(
    () =>
      (editable ? policies : license?.policies)?.map((policy) => ({
        id: policy._id,
        name: policy.name,
        description: policy.description,
      })),
    [editable, license, policies]
  )

  if (isLoading) {
    return null
  }

  if (!license) return null

  return (
    <PageContainer>
      <Stack spacing={4}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h2">{license.name}</Typography>
          {authUser?.roles.includes(Role.SuperAdmin) && (
            <Stack direction="row" spacing={2}>
              <Button color="error" onClick={() => setOpenDeleteConfirmationDialog(true)} variant="contained">
                {t('common:button.delete.label')}
              </Button>
              <Button onClick={() => setEditable(!editable)} variant="contained">
                {!editable ? t('common:button.modify.label') : t('common:button.cancel.label')}
              </Button>
            </Stack>
          )}
        </Stack>
        <Stack spacing={3}>
          <Paper>
            <Box p={3}>
              <LicenseForm
                key={editable ? 'editable' : 'readOnly'}
                initialValues={initialValues}
                isSubmitting={isUpdatingLicense}
                onSubmit={async (values) => {
                  try {
                    await updateLicense({ id: license._id, ...values }).unwrap()
                    setEditable(false)
                  } catch (error) {
                    enqueueSnackbar(getApiErrorMessage(error, t('notification.policy.update.error')), { variant: 'error' })
                  }
                }}
                policies={policiesData}
                readOnly={!editable}
              />
            </Box>
          </Paper>
        </Stack>
      </Stack>
      <DeleteConfirmationDialog
        isDeleting={isDeletingLicense}
        maxWidth={800}
        onClose={() => {
          setOpenDeleteConfirmationDialog(false)
        }}
        onDelete={async () => {
          await deleteLicense(license._id).unwrap()
          setOpenDeleteConfirmationDialog(false)
        }}
        open={openDeleteConfirmationDialog}
        title={t('dialog.deleteConfirmation.license.title')}
        message={t('dialog.deleteConfirmation.license.message')}
      />
    </PageContainer>
  )
}
